import { useMemo } from 'react';
import { pipe, Option } from 'effect';
import type { AgGridReactProps } from 'ag-grid-react';
import type { ColDef, ProcessCellForExportParams } from 'ag-grid-community';

import type { BankFeedTransactionRow } from 'domain/bankFeed/schemas';

// colDef.type is `string | string[] | undefined` — normalise to a flat array so the clipboard
// serializer can branch on the column's semantic `type` (delivered by the BE column-headers
// endpoint; the per-type colDef config — renderers, formatters — lives in useAgGridColumnTypes).
const colTypes = (colDef: ColDef): readonly string[] => {
  const { type } = colDef;
  return Array.isArray(type) ? type : type ? [type] : [];
};

// Unicode bidi formatting controls — LRM/RLM (U+200E/F), embeddings & overrides (U+202A–202E),
// and the isolates LRI/RLI/FSI/PDI (U+2066–2069). Invisible direction hints, not data — see the
// clipboard fallback below for why they are stripped on copy.
const BIDI_CONTROLS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const stripBidiControls = (value: string): string => value.replace(BIDI_CONTROLS, '');

// Clipboard (Cmd+C) serializer — DA-19529. AG Grid v34 copies the *display* value by default,
// which yields "[object Object]" for the two object-valued custom columns and a thousands-grouped
// "2,080.00" for amounts (Excel reads the grouping comma as a decimal in comma-locales → the cell
// lands as text). Override those three:
//   • account            → the bank/account `name`
//   • transaction_status → the BE display `label`
//   • amount             → the RAW numeric string (no grouping) so Excel parses it as a number
// Every other column (string / date / select) keeps AG Grid's formatted display value. The
// selected-cells Sum panel (DA-19100) reads raw values via getCellValue, not the clipboard, so it
// is unaffected.
const processCellForClipboard = (params: ProcessCellForExportParams<BankFeedTransactionRow>): string => {
  const types = colTypes(params.column.getColDef());
  const data = params.node?.data;

  if (types.includes('account')) {
    return pipe(
      Option.fromNullable(data?.account),
      Option.map((account) => account.name),
      Option.getOrElse(() => ''),
    );
  }

  if (types.includes('transaction_status')) {
    return pipe(
      Option.fromNullable(data?.status),
      Option.map((status) => status.label),
      Option.getOrElse(() => ''),
    );
  }

  if (types.includes('amount')) {
    return pipe(
      Option.fromNullable(params.column.getColDef().field),
      Option.flatMap((field) => Option.fromNullable(data?.[field])),
      Option.map(String),
      Option.getOrElse(() => ''),
    );
  }

  // Every other column (string / date / select) keeps its formatted display value. When a custom
  // processCellForClipboard is set, AG Grid passes the RAW value here (not the display value it uses
  // by default), so re-apply the column's valueFormatter via params.formatValue — otherwise a date
  // copies as the raw ISO string instead of the company DD/MM/YYYY format. Then strip bidi controls:
  // the BE wraps mixed-direction free text (today only `merchant_address` — a Hebrew street + a
  // Latin country code) in directional isolates to force a stable render order in the grid; copied
  // verbatim those invisible controls are junk in Excel (boxes / broken lookups). Each spreadsheet
  // cell is its own bidi paragraph and re-derives direction from the first strong char, so dropping
  // them does not change the pasted order. The on-screen grid keeps them (only the clipboard is cleaned).
  return pipe(
    Option.fromNullable(params.value),
    Option.flatMap((value) => Option.fromNullable(params.formatValue(value))),
    Option.map(String),
    Option.map(stripBidiControls),
    Option.getOrElse(() => ''),
  );
};

const useAgGridProps = (): AgGridReactProps => {
  return useMemo<AgGridReactProps>(
    () => ({
      rowModelType: 'serverSide',
      pagination: true,
      paginationPageSize: 1000,
      paginationPageSizeSelector: [100, 1000, 5000],
      // Drop AG Grid's default full-width "Loading…" banner row during SSRM block fetch —
      // matches Workspace / VendorExpenses / VendorBasedApprovals / JEInsights. Cells render
      // empty placeholders while data arrives, which keeps row geometry stable and avoids the
      // layout jump when the banner is swapped out for the real rows.
      suppressServerSideFullWidthLoadingRow: true,
      // Must equal paginationPageSize — cursor-pagination invariant (re-synced at runtime in
      // BankFeedGrid's `onPaginationChanged`).
      cacheBlockSize: 1000,
      // Force a fresh BE fetch on every navigation — bank-feed transactions mutate (new records
      // stream in, status / classification updates land asynchronously) and cursor pagination
      // can only walk sequentially anyway, so caching adjacent blocks buys no UX speed but does
      // serve stale data. Capping the cache at the single visible block keeps the view aligned
      // with server truth.
      maxBlocksInCache: 1,
      // Match Documents / VendorBasedApprovals grids — workspace's `CustomSetColumnFilter`
      // chrome (`ag-filter-wrapper ag-focus-managed` form classes) and our shared
      // `setExelModeFilter` / `addFilters` helpers are wired for the legacy column menu.
      // The default `'new'` menu re-renders filters inside a built-in `FilterWrapperComp`
      // that injects extra Apply/Reset chrome around custom filters.
      columnMenu: 'legacy',
      // Drag-select a range of cells — feeds the selected-cells Sum status panel
      // (DA-19100). v34 API; the deprecated `enableRangeSelection` flag used by the
      // Workspace grid is intentionally NOT copied here.
      cellSelection: true,
      // Footer panel showing the sum of the drag-selected amount cells (DA-19100).
      // `bankFeedSumStatusPanel` is registered in BankFeedGrid's `gridComponents.ts`.
      statusBar: {
        statusPanels: [{ statusPanel: 'bankFeedSumStatusPanel' }],
      },
      tooltipShowMode: 'whenTruncated',
      tooltipShowDelay: 0,
      suppressContextMenu: true,
      suppressMoveWhenColumnDragging: true,
      animateRows: false,
      scrollbarWidth: 8,
      alwaysShowHorizontalScroll: true,
      alwaysShowVerticalScroll: true,
      processCellForClipboard,
      defaultColDef: {
        suppressHeaderMenuButton: true,
        filter: true,
        sortable: true,
        resizable: true,
        minWidth: 120,
      },
    }),
    [],
  );
};

export default useAgGridProps;
